/** Environment variables: keys, values, versions, resolve, import/export and requirements. */
import { z } from 'zod';
import { reservedEnvKeyMessage, reservedEnvKeyRule } from '../env-keys.js';
import { EnvValueType, EnvVisibility, Platform, uuid } from './common.js';

export const envKey = z.string().regex(/^[A-Z_][A-Z0-9_]*$/, 'uppercase letters, digits and underscores').max(256);

/**
 * A key a variable may be written with: an `envKey` that is not a reserved name (`RESERVED_ENV_KEYS`: process control
 * such as `PATH` or `NODE_OPTIONS`, `HANGAR_*`, `EAS_*`, `EXPO_TOKEN`…). Used wherever a key is written (env var
 * create, trigger overrides, requirements); reading and observing (`envKey`) stay unrestricted. The issue carries
 * `params.code: RESERVED_KEY`, which the API reports as the error code.
 */
export const definableEnvKey = envKey.refine((key) => !/^[A-Z_][A-Z0-9_]*$/.test(key) || reservedEnvKeyRule(key) === undefined, {
  error: (issue) => {
    const key = String(issue.input);
    const rule = reservedEnvKeyRule(key);
    return rule ? reservedEnvKeyMessage(key, rule) : `${key} is a reserved name`;
  },
  params: { code: 'RESERVED_KEY' },
});

/**
 * User-written regular expressions (env requirement `pattern`, env assertion `pattern`) are at most this long. They
 * run on Hangar's linear-time engine (packages/domain/src/safe-regex.ts: RE2 syntax, no backreferences or lookaround),
 * which also checks them when they are saved.
 */
export const ENV_PATTERN_MAX_LENGTH = 200;
/** At most this many env assertions per profile. */
export const ENV_ASSERTIONS_MAX = 50;

/** Exactly one of `value` or `valueBase64` (api/openapi.yaml EnvValueInput). */
export const EnvValueInput = z.object({
  value: z.string().max(65536).optional(),
  valueBase64: z.string().max(90000).optional(),
}).refine((v) => (v.value === undefined) !== (v.valueBase64 === undefined), { message: 'exactly one of value or valueBase64' });

export const EnvVarCreateRequest = EnvValueInput.safeExtend({
  projectId: uuid.optional(),
  appId: uuid.optional(),
  environment: z.string().max(64).optional(),
  platform: Platform.optional(),
  key: definableEnvKey,
  visibility: EnvVisibility.default('plain'),
  valueType: EnvValueType.default('string'),
  configTime: z.boolean().default(false),
  description: z.string().max(500).optional(),
  note: z.string().max(500).optional(),
});
export type EnvVarCreateRequest = z.infer<typeof EnvVarCreateRequest>;

export const EnvVarPatch = z.object({
  description: z.string().max(500).nullable().optional(),
  visibility: EnvVisibility.optional(),
  valueType: EnvValueType.optional(),
  configTime: z.boolean().optional(),
}).strict();
export type EnvVarPatch = z.infer<typeof EnvVarPatch>;

export const EnvVarVersionCreateRequest = EnvValueInput.safeExtend({
  note: z.string().max(500).optional(),
  expectedCurrentVersion: z.number().int().min(1).optional(),
});
export type EnvVarVersionCreateRequest = z.infer<typeof EnvVarVersionCreateRequest>;

export const EnvOverride = z.object({ key: definableEnvKey, value: z.string().max(65536), visibility: EnvVisibility.default('sensitive') });
export type EnvOverride = z.infer<typeof EnvOverride>;

export const EnvResolveRequest = z.object({
  environment: z.string().max(64),
  platform: Platform,
  profile: z.string().max(64).optional(),
  overrides: z.array(EnvOverride).max(50).optional(),
  ciVariables: z.array(z.string().max(256)).max(500).optional(),
});
export type EnvResolveRequest = z.infer<typeof EnvResolveRequest>;

/** `EnvVarRevealRequest`. */
export const EnvVarRevealRequest = z.object({
  versionId: uuid.optional(),
  reason: z.string().min(3).max(500),
});
export type EnvVarRevealRequest = z.infer<typeof EnvVarRevealRequest>;

/** `EnvImportRequest`. */
export const EnvImportRequest = z.object({
  projectId: uuid.optional(),
  appId: uuid.optional(),
  environment: z.string().max(64).optional(),
  platform: Platform.optional(),
  content: z.string().max(262144),
  defaultVisibility: EnvVisibility.default('sensitive'),
  visibilityOverrides: z.record(z.string(), EnvVisibility).default({}),
  onConflict: z.enum(['skip', 'new_version', 'fail']).default('new_version'),
  dryRun: z.boolean().default(false),
  note: z.string().max(500).optional(),
});
export type EnvImportRequest = z.infer<typeof EnvImportRequest>;

/** `EnvExportRequest`. */
export const EnvExportRequest = z.object({
  environment: z.string().max(64),
  platform: Platform,
  profile: z.string().max(64).optional(),
  format: z.enum(['dotenv', 'json', 'shell']).default('dotenv'),
  includeSensitive: z.boolean().default(false),
  keys: z.array(envKey).max(500).optional(),
});
export type EnvExportRequest = z.infer<typeof EnvExportRequest>;

/** One `EnvRequirement` as written by `PUT /apps/{appId}/env-requirements`. */
export const EnvRequirementWrite = z.object({
  id: uuid.optional(),
  key: definableEnvKey,
  required: z.boolean().default(true),
  /** Linear-time engine, RE2 syntax (see `ENV_PATTERN_MAX_LENGTH`); the API also compiles it when it is saved. */
  pattern: z.string().max(ENV_PATTERN_MAX_LENGTH).nullable().optional(),
  environments: z.array(z.string().max(64)).min(1).nullable().optional(),
  platforms: z.array(Platform).min(1).nullable().optional(),
  expectedVisibility: EnvVisibility.nullable().optional(),
  description: z.string().max(500).nullable().optional(),
});
export const EnvRequirementsWrite = z.object({ requirements: z.array(EnvRequirementWrite).max(200) });
export type EnvRequirementsWrite = z.infer<typeof EnvRequirementsWrite>;
